import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getAllNotifications,
  updateFCMToken,
  deleteFCMToken,
  markNotificationAsRead,
  deleteNotification,
} from '../../api/notificationService';

export const getAllNotificationsThunk = createAsyncThunk(
  'notification/getAllNotifications',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllNotifications();
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch notifications');
    }
  }
);

export const updateFCMTokenThunk = createAsyncThunk(
  'notification/updateFCMToken',
  async ({ token }, { rejectWithValue }) => {
    try {
      const response = await updateFCMToken(token);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update token');
    }
  }
);

export const deleteFCMTokenThunk = createAsyncThunk(
  'notification/deleteFCMToken',
  async (_, { rejectWithValue }) => {
    try {
      await deleteFCMToken();
      return { success: true };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete token');
    }
  }
);

export const markAsReadThunk = createAsyncThunk(
  'notification/markAsRead',
  async ({ notificationId }, { rejectWithValue }) => {
    try {
      await markNotificationAsRead(notificationId);
      return notificationId;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to mark as read');
    }
  }
);

export const deleteNotificationThunk = createAsyncThunk(
  'notification/deleteNotification',
  async ({ notificationId }, { rejectWithValue }) => {
    try {
      await deleteNotification(notificationId);
      return notificationId;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete notification');
    }
  }
);

const initialState = {
  list: [],
  loading: false,
  error: null,
  unreadCount: 0,
  permission: 'default', // 'granted', 'denied', 'default'
  fcmToken: null,
};

const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    setPermission: (state, action) => {
      state.permission = action.payload;
    },
    setFCMToken: (state, action) => {
      state.fcmToken = action.payload;
    },
    updateUnreadCount: (state) => {
      state.unreadCount = state.list.filter((n) => n.status === 'unread').length;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Get All Notifications
    builder.addCase(getAllNotificationsThunk.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getAllNotificationsThunk.fulfilled, (state, action) => {
      state.loading = false;
      state.list = action.payload;
      state.unreadCount = action.payload.filter((n) => n.status === 'unread')
        .length;
    });
    builder.addCase(getAllNotificationsThunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Update FCM Token
    builder.addCase(updateFCMTokenThunk.pending, (state) => {
      state.error = null;
    });
    builder.addCase(updateFCMTokenThunk.fulfilled, (state) => {
      // Token updated successfully
    });
    builder.addCase(updateFCMTokenThunk.rejected, (state, action) => {
      state.error = action.payload;
    });

    // Delete FCM Token
    builder.addCase(deleteFCMTokenThunk.pending, (state) => {
      state.error = null;
    });
    builder.addCase(deleteFCMTokenThunk.fulfilled, (state) => {
      state.fcmToken = null;
    });
    builder.addCase(deleteFCMTokenThunk.rejected, (state, action) => {
      state.error = action.payload;
    });

    // Mark as Read
    builder.addCase(markAsReadThunk.fulfilled, (state, action) => {
      const notification = state.list.find((n) => n.userNotificationID === action.payload);
      if (notification) {
        notification.status = 'read';
        state.unreadCount = state.list.filter((n) => n.status === 'unread')
          .length;
      }
    });

    // Delete Notification
    builder.addCase(deleteNotificationThunk.fulfilled, (state, action) => {
      state.list = state.list.filter(
        (n) => n.userNotificationID !== action.payload
      );
      state.unreadCount = state.list.filter((n) => n.status === 'unread')
        .length;
    });
  },
});

export const {
  setPermission,
  setFCMToken,
  updateUnreadCount,
  clearError,
} = notificationSlice.actions;

export default notificationSlice.reducer;
